import { useTranslation } from 'react-i18next';

import { cn } from '~/lib/cn';
import type { TrackPerformanceItem } from '~/modules/label-perf/types';
import { formatNumberSuffix } from '~/modules/label-perf/utils/format';

import { getVintage } from '../utils';
import { ArtistLink } from './ArtistLink';
import { PerformanceDeltaLabel } from './PerformanceDeltaLabel';
import { TimeRangeLabel } from './TimeRangeLabel';

interface TopBlockProps {
  block: TrackPerformanceItem | null;
  index: number;
  loading: boolean;
  className?: string;
}

function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('animate-pulse rounded-sm bg-level-05', className)} />
  );
}

Skeleton.displayName = 'Skeleton';

export function TopBlock({ block, index, loading, className }: TopBlockProps) {
  const { t } = useTranslation('label-perf');

  if (loading) {
    return (
      <div
        className={cn(
          'h-[217px] w-full rounded-sm border p-md bg-level-02 border-emphasis-subtle',
          className,
        )}
        data-testid="block-wrapper"
      >
        <div className="mb-md flex justify-between font-semibold">
          <Skeleton className="h-[13px] w-[20px]" />
          <Skeleton className="h-[13px] w-[80px]" />
        </div>

        <div className="border-b pb-md border-emphasis-subtle">
          <Skeleton className="mb-xs h-[54px] w-[150px]" />
          <Skeleton className="h-[16px] w-[100px]" />
        </div>

        <div className="mt-md">
          <div className="mb-xs flex items-center">
            <Skeleton className="mr-xs h-[16px] w-[80px]" />
            <Skeleton className="h-[16px] w-[24px]" />
          </div>
          <div className="flex items-center">
            <Skeleton className="mr-sm h-[24px] w-[64px]" />
            <Skeleton className="h-[24px] w-[32px]" />
          </div>
        </div>
      </div>
    );
  }

  return block ? (
    <div
      className={cn(
        'rounded-sm border p-md bg-level-02 border-emphasis-subtle',
        className,
      )}
      data-testid="block-wrapper"
    >
      <div className="mb-md flex justify-between font-semibold">
        <div className="flex items-center rounded-xs px-sm py-xs bg-level-05">
          <p className="text-caption-md text-white">#{index}</p>
        </div>
        <div className="rounded-xs px-sm py-xs bg-level-05">
          <p className="text-caption-md text-white">
            {t(`table.${getVintage(block.releaseDate)}`)}
          </p>
        </div>
      </div>

      <div className="border-b pb-md border-emphasis-subtle">
        <p className="truncate text-display-sm font-bold">
          {block.familyTitle || '--'}
        </p>
        <ArtistLink
          artist={block.artist}
          relatedArtists={block.relatedArtists}
        />
      </div>

      <div className="mt-md">
        <div className="flex items-end gap-xs">
          <span className="text-body-xs">{t('table.rtdTwStreams')}</span>
          <TimeRangeLabel timeRange="TW" />
        </div>
        <div className="flex">
          <span className="mr-sm text-display-sm font-bold">
            {formatNumberSuffix(block.streamsTw) || '--'}
          </span>
          <PerformanceDeltaLabel delta={block.streamsWow} />
        </div>
      </div>
    </div>
  ) : null;
}

TopBlock.displayName = 'TopBlock';
